import React, { useMemo, useState } from 'react';
import {
  getInterventionDisplayLines,
  type InstanceRow,
} from '../../components/interventions/instanceModel';
import { type MainMapView } from '../mapUtils';
import { Batiment, Vehicule } from '../../../../shared/types/game';
import { buildAvailableUnitsRows } from '../unitsRows';

interface VehicleStatusMetrics {
  total: number;
  pret: number;
  mission: number;
  maintenance: number;
  panne: number;
  premium: number;
  totalKm: number;
  avgKm: number;
  totalCapacity: number;
  readinessPercent: number;
}

interface PersonnelMetrics {
  total: number;
  actif: number;
  inactif: number;
  avgAge: number;
  nearRetirement: number;
  premium: number;
  activeRate: number;
}

interface AvailableUnitRow {
  id: number;
  unit: string;
  type: string;
  service: string;
  crew: string;
  state: string;
  eta: string;
}

interface ResourcesViewProps {
  vehicleStatusMetrics: VehicleStatusMetrics;
  personnelMetrics: PersonnelMetrics;
  resourceAlerts: string[];
  topMileageVehicles: Vehicule[];
  activeInterventions: InstanceRow[];
  /** Données brutes : la vue filtre elle-même, avec ses propres filtres locaux. */
  playerVehicles: Vehicule[];
  buildings: Batiment[];
  buildingFilterOptions: { id: number; name: string }[];
  vehicleTypeFilterOptions: string[];
  setMainView: (view: MainMapView) => void;
  setSelectedInterventionId: (id: number) => void;
  openDedicatedMainView: (view: MainMapView) => void;
  openInterventionsSynoptic: () => void;
}

const ResourcesView: React.FC<ResourcesViewProps> = ({
  vehicleStatusMetrics,
  personnelMetrics,
  resourceAlerts,
  topMileageVehicles,
  activeInterventions,
  playerVehicles,
  buildings,
  buildingFilterOptions,
  vehicleTypeFilterOptions,
  setMainView,
  setSelectedInterventionId,
  openDedicatedMainView,
  openInterventionsSynoptic,
}) => {
  // Étape 2b : filtres LOCAUX à la vue — ils ne fuient plus vers les autres onglets.
  const [unitsSearch, setUnitsSearch] = useState('');
  const [buildingFilter, setBuildingFilter] = useState<number | 'all'>('all');
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState<string>('all');
  const [unitsFilter, setUnitsFilter] = useState<
    'all' | 'disponible' | 'en_mission' | 'en_maintenance'
  >('all');

  const availableUnitsRows: AvailableUnitRow[] = useMemo(
    () =>
      buildAvailableUnitsRows(playerVehicles, buildings, {
        buildingFilter,
        vehicleTypeFilter,
        unitsFilter,
        unitsSearch,
      }),
    [playerVehicles, buildings, buildingFilter, vehicleTypeFilter, unitsFilter, unitsSearch],
  );

  return (
  <div className="grid h-full min-h-0 grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1.45fr),minmax(0,1fr)]">
    <section className="min-h-0 rounded-lg border border-slate-700/75 bg-slate-900/90 p-3 shadow-xl shadow-black/30">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-emerald-200">Vue ressources avancée</h2>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={openInterventionsSynoptic}
            className="rounded border border-red-500/45 bg-red-600/20 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-red-100 hover:bg-red-600/30"
          >
            Synoptique
          </button>
          <span className="rounded border border-emerald-500/50 bg-emerald-500/15 px-2 py-1 text-[10px] font-semibold text-emerald-200">
            {availableUnitsRows.length} unités filtrées
          </span>
          <button
            type="button"
            onClick={() => openDedicatedMainView('resources')}
            className="rounded border border-slate-600/70 bg-slate-800/70 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-200 hover:bg-slate-700/80"
          >
            Ouvrir en page
          </button>
        </div>
      </div>

      <div className="mb-3 grid grid-cols-2 gap-2 text-[11px] xl:grid-cols-4">
        <div className="rounded border border-slate-700/70 bg-slate-950/70 p-2">
          <p className="text-[10px] uppercase tracking-wide text-slate-500">Flotte totale</p>
          <p className="mt-1 text-lg font-semibold text-slate-100">{vehicleStatusMetrics.total}</p>
        </div>
        <div className="rounded border border-emerald-500/35 bg-emerald-950/20 p-2">
          <p className="text-[10px] uppercase tracking-wide text-emerald-300">Prêts au départ</p>
          <p className="mt-1 text-lg font-semibold text-emerald-200">{vehicleStatusMetrics.pret}</p>
        </div>
        <div className="rounded border border-amber-500/35 bg-amber-950/20 p-2">
          <p className="text-[10px] uppercase tracking-wide text-amber-300">En mission</p>
          <p className="mt-1 text-lg font-semibold text-amber-200">{vehicleStatusMetrics.mission}</p>
        </div>
        <div className="rounded border border-sky-500/35 bg-sky-950/20 p-2">
          <p className="text-[10px] uppercase tracking-wide text-sky-300">Maint./panne</p>
          <p className="mt-1 text-lg font-semibold text-sky-200">{vehicleStatusMetrics.maintenance + vehicleStatusMetrics.panne}</p>
        </div>
      </div>

      <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
        <input
          type="search"
          value={unitsSearch}
          onChange={(e) => setUnitsSearch(e.target.value)}
          placeholder="Rechercher une unité..."
          className="rounded border border-slate-700/70 bg-slate-900/85 px-2 py-1.5 text-[11px] text-slate-100 placeholder:text-slate-500 outline-none focus:border-emerald-500/60"
        />
        <select
          value={buildingFilter}
          onChange={(e) => {
            const v = e.target.value;
            setBuildingFilter(v === 'all' ? 'all' : Number(v));
          }}
          className="rounded border border-slate-700/70 bg-slate-900/85 px-2 py-1.5 text-[11px] text-slate-100 outline-none focus:border-emerald-500/60"
        >
          <option value="all">Tous les bâtiments</option>
          {buildingFilterOptions.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.name}
            </option>
          ))}
        </select>
        <select
          value={vehicleTypeFilter}
          onChange={(e) => setVehicleTypeFilter(e.target.value)}
          className="rounded border border-slate-700/70 bg-slate-900/85 px-2 py-1.5 text-[11px] text-slate-100 outline-none focus:border-emerald-500/60"
        >
          <option value="all">Tous les types</option>
          {vehicleTypeFilterOptions.map((typeName) => (
            <option key={typeName} value={typeName}>
              {typeName}
            </option>
          ))}
        </select>
        <select
          value={unitsFilter}
          onChange={(e) => setUnitsFilter(e.target.value as 'all' | 'disponible' | 'en_mission' | 'en_maintenance')}
          className="rounded border border-slate-700/70 bg-slate-900/85 px-2 py-1.5 text-[11px] text-slate-100 outline-none focus:border-emerald-500/60"
        >
          <option value="all">Statut: tous</option>
          <option value="disponible">Prêts</option>
          <option value="en_mission">Mission</option>
          <option value="en_maintenance">Maintenance</option>
        </select>
      </div>

      <div className="overflow-hidden rounded-md border border-slate-700/70 bg-slate-950/85">
        <div className="grid grid-cols-[minmax(0,1.2fr),minmax(0,0.85fr),minmax(0,0.85fr),58px,minmax(0,1fr),70px] border-b border-slate-700/70 bg-slate-900/70 px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400">
          <span>Unité</span>
          <span>Type</span>
          <span>Service</span>
          <span className="text-center">Eq.</span>
          <span>État</span>
          <span className="text-right">ETA</span>
        </div>
        <div className="max-h-[54vh] overflow-y-auto text-[11px]">
          {availableUnitsRows.map((row) => (
            <div
              key={row.id}
              className="grid grid-cols-[minmax(0,1.2fr),minmax(0,0.85fr),minmax(0,0.85fr),58px,minmax(0,1fr),70px] items-center gap-1 border-b border-slate-800/65 px-2 py-2 last:border-b-0"
            >
              <span className="truncate pr-1 text-slate-200">{row.unit}</span>
              <span className="truncate text-slate-400">{row.type}</span>
              <span className="truncate text-slate-300">{row.service}</span>
              <span className="text-center font-semibold text-slate-200">{row.crew}</span>
              <span className={row.state === 'PRÊT' ? 'text-emerald-300' : 'text-amber-300'}>{row.state}</span>
              <span className="text-right text-slate-300">{row.eta}</span>
            </div>
          ))}
          {availableUnitsRows.length === 0 ? (
            <p className="px-2 py-4 text-center text-slate-500">Aucune unité correspondant au filtre</p>
          ) : null}
        </div>
      </div>
    </section>

    <section className="grid min-h-0 grid-rows-[auto,auto,1fr] gap-3">
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <div className="rounded-lg border border-slate-700/80 bg-slate-900/90 p-3">
          <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400">Disponibilité flotte</p>
          <p className="mt-1 text-lg font-semibold text-emerald-300">{vehicleStatusMetrics.readinessPercent}%</p>
          <p className="text-[10px] text-slate-400">{vehicleStatusMetrics.pret}/{vehicleStatusMetrics.total} prêts</p>
        </div>
        <div className="rounded-lg border border-slate-700/80 bg-slate-900/90 p-3">
          <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400">Taux RH actif</p>
          <p className="mt-1 text-lg font-semibold text-emerald-300">{personnelMetrics.activeRate}%</p>
          <p className="text-[10px] text-slate-400">{personnelMetrics.actif}/{personnelMetrics.total} agents actifs</p>
        </div>
        <div className="rounded-lg border border-slate-700/80 bg-slate-900/90 p-3">
          <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400">Km moyen flotte</p>
          <p className="mt-1 text-lg font-semibold text-slate-100">{vehicleStatusMetrics.avgKm.toLocaleString('fr-FR')}</p>
          <p className="text-[10px] text-slate-400">Total {vehicleStatusMetrics.totalKm.toLocaleString('fr-FR')} km</p>
        </div>
        <div className="rounded-lg border border-slate-700/80 bg-slate-900/90 p-3">
          <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400">Capacité équipages</p>
          <p className="mt-1 text-lg font-semibold text-slate-100">{vehicleStatusMetrics.totalCapacity}</p>
          <p className="text-[10px] text-slate-400">{vehicleStatusMetrics.premium} unités premium</p>
        </div>
      </div>

      <div className="rounded-lg border border-slate-700/80 bg-slate-900/90 p-3">
        <p className="mb-2 text-[10px] uppercase tracking-[0.12em] text-amber-300">Alertes opérationnelles</p>
        <div className="space-y-1.5 text-[11px]">
          {resourceAlerts.length > 0 ? (
            resourceAlerts.map((alert) => (
              <p key={alert} className="rounded border border-amber-500/30 bg-amber-950/25 px-2 py-1 text-amber-100">
                {alert}
              </p>
            ))
          ) : (
            <p className="rounded border border-emerald-500/30 bg-emerald-950/20 px-2 py-1 text-emerald-200">
              Situation stable : aucune tension critique detectee.
            </p>
          )}
        </div>
      </div>

      <div className="grid min-h-0 grid-rows-[auto,1fr] gap-2">
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg border border-slate-700/80 bg-slate-900/90 p-2.5">
            <p className="text-[10px] uppercase tracking-wide text-slate-400">Personnel inactif</p>
            <p className="mt-1 text-sm font-semibold text-slate-100">{personnelMetrics.inactif}</p>
          </div>
          <div className="rounded-lg border border-slate-700/80 bg-slate-900/90 p-2.5">
            <p className="text-[10px] uppercase tracking-wide text-slate-400">Age moyen</p>
            <p className="mt-1 text-sm font-semibold text-slate-100">{personnelMetrics.avgAge} ans</p>
          </div>
        </div>
        <div className="grid min-h-0 grid-cols-1 gap-2 xl:grid-cols-2">
          <div className="min-h-0 rounded-lg border border-slate-700/80 bg-slate-900/90 p-2.5">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-[10px] uppercase tracking-wide text-slate-400">
                Interventions actives ({activeInterventions.length})
              </p>
              <button
                type="button"
                onClick={openInterventionsSynoptic}
                className="rounded border border-red-500/40 bg-red-600/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-100 hover:bg-red-600/25"
              >
                Gestion complète
              </button>
            </div>
            <div className="max-h-[24vh] space-y-1.5 overflow-y-auto">
              {activeInterventions.map((inv) => {
                const lines = getInterventionDisplayLines(inv, 'Intervention');
                return (
                  <button
                    key={inv.id}
                    type="button"
                    onClick={() => {
                      setSelectedInterventionId(inv.id);
                      setMainView('dispatch');
                    }}
                    className="w-full rounded border border-slate-700/70 bg-slate-950/70 px-2 py-1.5 text-left hover:bg-slate-800/80"
                  >
                    <p className="truncate text-xs font-semibold text-slate-100">{lines.headline}</p>
                    <p className="truncate text-[10px] text-slate-400">{lines.subline || 'Adresse non précisée'}</p>
                  </button>
                );
              })}
              {activeInterventions.length === 0 ? (
                <p className="text-[11px] text-slate-500">Aucune intervention active.</p>
              ) : null}
            </div>
          </div>
          <div className="min-h-0 rounded-lg border border-slate-700/80 bg-slate-900/90 p-2.5">
            <p className="mb-2 text-[10px] uppercase tracking-wide text-slate-400">Top kilométrage</p>
            <div className="max-h-[24vh] space-y-1.5 overflow-y-auto text-[11px]">
              {topMileageVehicles.length > 0 ? (
                topMileageVehicles.map((v) => (
                  <div key={v.id} className="rounded border border-slate-700/70 bg-slate-950/70 px-2 py-1.5">
                    <p className="truncate font-semibold text-slate-100">{v.nom || `Unité ${v.id}`}</p>
                    <p className="text-[10px] text-slate-400">
                      {String(v.type || 'Type inconnu')} - {Number(v.kilometrage || 0).toLocaleString('fr-FR')} km
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-slate-500">Aucun véhicule enregistré.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
  );
};

export default ResourcesView;
